/**
 * CV Sold Out Fix v4
 * 
 * Fixes stale Liquid availability for newer Dawn themes that use
 * ProductSelectEvent + server-fetched HTML updates.
 *
 * Key fixes:
 * 1. Strips "-N" suffix from input names to match Storefront API option names
 * 2. Uses document-level event delegation (survives element replacement)
 * 3. MutationObserver watches for variant-selects element replacement
 * 4. Re-applies fix after every server HTML update with multiple timeouts
 * 5. Correctly computes which options should be enabled based on real inventory
 */
(function() {
  'use strict';

  var CV_PRODUCT_DATA = null;
  var CV_AVAILABLE_VARIANTS = [];
  var CV_HANDLE = null;
  var CV_FIX_APPLIED = 0;
  var CV_FIX_IN_PROGRESS = false;
  var CV_MUTATION_PENDING = false;
  var CV_MAX_FIXES = 200;

  function getStorefrontToken() {
    var scripts = document.querySelectorAll('script[type="application/json"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent);
        if (data.accessToken) return data.accessToken;
      } catch (e) {}
    }
    return null;
  }

  function normalizeOptionName(name) {
    if (!name) return '';
    // Remove whitespace/newlines, then strip trailing "-N" suffix (e.g., "Color-1" -> "Color")
    var cleaned = name.replace(/\s+/g, '').replace(/\n/g, '');
    // Also handle the case where the name itself contains hyphens (e.g., "T-Shirt-Size-2")
    // We strip the LAST "-N" where N is a number
    cleaned = cleaned.replace(/-\d+$/, '');
    return cleaned.toLowerCase();
  }

  function fetchProductData(handle) {
    if (CV_PRODUCT_DATA) return Promise.resolve(CV_PRODUCT_DATA);
    var token = getStorefrontToken();
    if (!token) return Promise.resolve(null);

    var query = '{ productByHandle(handle: "' + handle + '") { title availableForSale variants(first: 50) { edges { node { id availableForSale selectedOptions { name value } } } } } }';
    return fetch('/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      CV_PRODUCT_DATA = data.data && data.data.productByHandle;
      if (CV_PRODUCT_DATA) {
        var edges = CV_PRODUCT_DATA.variants ? CV_PRODUCT_DATA.variants.edges : [];
        CV_AVAILABLE_VARIANTS = [];
        for (var i = 0; i < edges.length; i++) {
          if (edges[i].node.availableForSale) {
            CV_AVAILABLE_VARIANTS.push(edges[i].node);
          }
        }
      }
      return CV_PRODUCT_DATA;
    })
    .catch(function() { return null; });
  }

  function getSelectedOptions() {
    var inputs = document.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
    var selected = {};
    for (var i = 0; i < inputs.length; i++) {
      var name = normalizeOptionName(inputs[i].name);
      selected[name] = inputs[i].value;
    }
    // Also check for select elements
    var selects = document.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      if (selects[j].value) {
        var selName = normalizeOptionName(selects[j].name);
        selected[selName] = selects[j].value;
      }
    }
    return selected;
  }

  function wouldOptionBeAvailable(optionName, optionValue, currentSelection) {
    var normName = normalizeOptionName(optionName);
    var testSelection = {};
    var keys = Object.keys(currentSelection);
    for (var i = 0; i < keys.length; i++) {
      testSelection[keys[i]] = currentSelection[keys[i]];
    }
    testSelection[normName] = optionValue;

    for (var j = 0; j < CV_AVAILABLE_VARIANTS.length; j++) {
      var variant = CV_AVAILABLE_VARIANTS[j];
      var variantOptions = {};
      var opts = variant.selectedOptions || [];
      for (var k = 0; k < opts.length; k++) {
        var optName = normalizeOptionName(opts[k].name);
        variantOptions[optName] = opts[k].value;
      }

      var match = true;
      var selKeys = Object.keys(testSelection);
      for (var m = 0; m < selKeys.length; m++) {
        var selKey = selKeys[m];
        if (testSelection[selKey] && variantOptions[selKey] !== testSelection[selKey]) {
          match = false;
          break;
        }
      }
      if (match) return true;
    }
    return false;
  }

  function findLabel(input) {
    if (input.id) {
      try {
        var label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (label) return label;
      } catch (e) {}
    }
    var sibling = input.nextElementSibling;
    while (sibling) {
      if (sibling.tagName === 'LABEL') return sibling;
      sibling = sibling.nextElementSibling;
    }
    var parent = input.parentElement;
    if (parent && parent.tagName === 'LABEL') return parent;
    if (parent) {
      var labelInParent = parent.querySelector('label');
      if (labelInParent) return labelInParent;
    }
    return null;
  }

  function applyVariantPickerFix() {
    if (!CV_PRODUCT_DATA || !CV_PRODUCT_DATA.availableForSale) return;
    if (!CV_AVAILABLE_VARIANTS || CV_AVAILABLE_VARIANTS.length === 0) return;

    // Prevent re-entrant calls and runaway loops
    if (CV_FIX_IN_PROGRESS) return;
    if (CV_FIX_APPLIED >= CV_MAX_FIXES) return;
    CV_FIX_IN_PROGRESS = true;

    CV_FIX_APPLIED++;
    var currentSelection = getSelectedOptions();

    // Fix all radio/checkbox inputs
    var allRadios = document.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    for (var i = 0; i < allRadios.length; i++) {
      var input = allRadios[i];

      // Skip inputs that aren't part of the variant picker
      var variantSelects = input.closest('variant-selects');
      if (!variantSelects) continue;

      var inputName = input.name;
      var inputValue = input.value;
      var shouldEnable = wouldOptionBeAvailable(inputName, inputValue, currentSelection);

      if (shouldEnable) {
        input.disabled = false;
        input.classList.remove('disabled');
        input.removeAttribute('disabled');

        var label = findLabel(input);
        if (label) {
          label.classList.remove('disabled');
        }

        var fieldset = input.closest('fieldset');
        if (fieldset) {
          fieldset.classList.remove('disabled');
        }
      } else {
        input.classList.add('disabled');
        var label2 = findLabel(input);
        if (label2) {
          label2.classList.add('disabled');
        }
      }
    }

    // Fix select elements
    var allSelects = document.querySelectorAll('select');
    for (var s = 0; s < allSelects.length; s++) {
      var sel = allSelects[s];
      var selVariantSelects = sel.closest('variant-selects');
      if (!selVariantSelects) continue;

      var options = sel.querySelectorAll('option');
      for (var o = 0; o < options.length; o++) {
        var shouldEnableOpt = wouldOptionBeAvailable(sel.name, options[o].value, currentSelection);
        if (shouldEnableOpt) {
          options[o].disabled = false;
          options[o].classList.remove('disabled');
        } else {
          options[o].disabled = true;
        }
      }
    }

    updateHiddenVariantId();
    fixSubmitButton();
    fixBadges();

    CV_FIX_IN_PROGRESS = false;
  }

  function updateHiddenVariantId() {
    var currentSelection = getSelectedOptions();

    for (var i = 0; i < CV_AVAILABLE_VARIANTS.length; i++) {
      var variant = CV_AVAILABLE_VARIANTS[i];
      var variantOptions = {};
      var opts = variant.selectedOptions || [];
      for (var k = 0; k < opts.length; k++) {
        var optName = normalizeOptionName(opts[k].name);
        variantOptions[optName] = opts[k].value;
      }

      var match = true;
      var selKeys = Object.keys(currentSelection);
      for (var m = 0; m < selKeys.length; m++) {
        var selKey = selKeys[m];
        if (currentSelection[selKey] && variantOptions[selKey] !== currentSelection[selKey]) {
          match = false;
          break;
        }
      }

      if (match) {
        var variantIdInputs = document.querySelectorAll('input[name="id"]');
        for (var j = 0; j < variantIdInputs.length; j++) {
          var newId = variant.id.replace('gid://shopify/ProductVariant/', '');
          if (variantIdInputs[j].value !== newId) {
            variantIdInputs[j].value = newId;
          }
        }
        break;
      }
    }
  }

  function fixSubmitButton() {
    var buttons = document.querySelectorAll('button[type="submit"], .product-form__submit');
    for (var j = 0; j < buttons.length; j++) {
      buttons[j].disabled = false;
      buttons[j].removeAttribute('disabled');
      var spans = buttons[j].querySelectorAll('span');
      for (var k = 0; k < spans.length; k++) {
        if (spans[k].textContent.trim() === 'Sold out' && !spans[k].classList.contains('sold-out-message')) {
          spans[k].textContent = 'Add to cart';
        }
        if (spans[k].classList.contains('sold-out-message')) {
          spans[k].classList.add('hidden');
        }
      }
    }
  }

  function fixBadges() {
    var allBadges = document.querySelectorAll('.badge');
    for (var i = 0; i < allBadges.length; i++) {
      if (allBadges[i].textContent.trim() === 'Sold out') {
        allBadges[i].style.display = 'none';
      }
    }
  }

  function patchVariantDataJSON() {
    // Patch the embedded variant JSON in the variant-selects element
    var variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    var script = variantSelects.querySelector('script[type="application/json"]');
    if (!script) return;

    try {
      var data = JSON.parse(script.textContent);
      // The embedded JSON is a single variant object (the currently selected one)
      // Patch its available field if it's a known available variant
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        if (data.id) {
          var variantIdStr = String(data.id);
          for (var i = 0; i < CV_AVAILABLE_VARIANTS.length; i++) {
            var avId = CV_AVAILABLE_VARIANTS[i].id.replace('gid://shopify/ProductVariant/', '');
            if (variantIdStr === avId && data.available === false) {
              data.available = true;
              script.textContent = JSON.stringify(data);
              break;
            }
          }
        }
      }
    } catch (e) {}
  }

  function fixProductPage() {
    var productForm = document.querySelector('.product-form, form[action*="/cart"], product-form');
    if (!productForm) return;

    var path = window.location.pathname;
    var match = path.match(/\/products\/([^\/\?]+)/);
    if (!match) return;
    CV_HANDLE = match[1];

    fetchProductData(CV_HANDLE).then(function(product) {
      if (!product) return;

      // Apply fix immediately
      patchVariantDataJSON();
      applyVariantPickerFix();

      // Use document-level event delegation for change events
      // This survives element replacement by updateOptionValues
      document.addEventListener('change', function(e) {
        if (!e.target) return;
        // Check if the change is on a variant picker input
        var variantSelects = e.target.closest('variant-selects');
        if (!variantSelects) return;

        // Apply fix with multiple timeouts to beat Dawn's async HTML updates
        setTimeout(applyVariantPickerFix, 0);
        setTimeout(applyVariantPickerFix, 100);
        setTimeout(applyVariantPickerFix, 300);
      });

      // MutationObserver to catch element replacement by updateOptionValues
      if (typeof MutationObserver !== 'undefined') {
        // Observe the parent of variant-selects for childList changes (element replacement)
        var vs = document.querySelector('variant-selects');
        var observeTarget = vs ? (vs.parentElement || vs) : document.body;
        
        var observer = new MutationObserver(function(mutations) {
          var needsFix = false;
          for (var i = 0; i < mutations.length; i++) {
            // Check for added nodes (new variant-selects element)
            if (mutations[i].addedNodes && mutations[i].addedNodes.length > 0) {
              for (var j = 0; j < mutations[i].addedNodes.length; j++) {
                var node = mutations[i].addedNodes[j];
                if (node.tagName === 'VARIANT-SELECTS' || (node.querySelector && node.querySelector('variant-selects'))) {
                  needsFix = true;
                  break;
                }
              }
            }
          }
          
          if (needsFix && !CV_MUTATION_PENDING) {
            CV_MUTATION_PENDING = true;
            patchVariantDataJSON();
            // Use setTimeout to break the mutation loop and debounce
            setTimeout(function() {
              applyVariantPickerFix();
              CV_MUTATION_PENDING = false;
            }, 50);
            setTimeout(function() {
              applyVariantPickerFix();
            }, 200);
          }
        });
        
        // Only observe childList (element replacement), NOT attribute changes
        // to avoid infinite loops from our own class modifications
        observer.observe(observeTarget, {
          childList: true,
          subtree: true
        });
      }
    });
  }

  function fixProductCard(card) {
    var badges = card.querySelectorAll('.badge');
    var hasSoldOut = false;
    var soldOutBadges = [];

    for (var i = 0; i < badges.length; i++) {
      if (badges[i].textContent.trim() === 'Sold out') {
        hasSoldOut = true;
        soldOutBadges.push(badges[i]);
      }
    }

    if (!hasSoldOut) return;

    var link = card.querySelector('.card__heading a, .full-unstyled-link');
    var href = link ? link.getAttribute('href') : '';
    var handle = href ? href.replace('/products/', '') : '';

    if (!handle) return;

    var token = getStorefrontToken();
    if (!token) return;

    var query = '{ productByHandle(handle: "' + handle + '") { title availableForSale } }';
    fetch('/api/2024-01/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query: query })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      var product = data.data && data.data.productByHandle;
      if (!product || !product.availableForSale) return;

      for (var i = 0; i < soldOutBadges.length; i++) {
        soldOutBadges[i].style.display = 'none';
      }

      var buttons = card.querySelectorAll('button[type="submit"]');
      for (var j = 0; j < buttons.length; j++) {
        buttons[j].disabled = false;
        var spans = buttons[j].querySelectorAll('span');
        for (var k = 0; k < spans.length; k++) {
          if (spans[k].textContent.trim() === 'Sold out' && !spans[k].classList.contains('sold-out-message')) {
            spans[k].textContent = 'Add to cart';
          }
          if (spans[k].classList.contains('sold-out-message')) {
            spans[k].classList.add('hidden');
          }
        }
      }
    })
    .catch(function() {});
  }

  function fixAllCards() {
    var cards = document.querySelectorAll('.card, .card--product');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].dataset.cvSoldoutFixed) continue;
      cards[i].dataset.cvSoldoutFixed = 'true';
      fixProductCard(cards[i]);
    }
  }

  function injectChatWidgetFix() {
    // Move InstaChat widget above the sticky CTA bar on mobile
    var style = document.createElement('style');
    style.id = 'cv-chat-widget-fix';
    style.textContent = [
      '@media (max-width: 990px) {',
      '  .instachat_float_widget_container_v_3_4,',
      '  #id_instachat_float_widget_container_v_3_4 {',
      '    bottom: 80px !important;',
      '    top: auto !important;',
      '  }',
      '  #id_instachat_float_widget_invitation_bubble {',
      '    bottom: 150px !important;',
      '    top: auto !important;',
      '  }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  function init() {
    injectChatWidgetFix();
    fixAllCards();
    fixProductPage();

    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].querySelectorAll) {
              var newCards = added[j].querySelectorAll('.card, .card--product');
              if (newCards.length > 0) {
                fixAllCards();
                break;
              }
            }
          }
        }
      });
      var grid = document.querySelector('#product-grid, .product-grid, .collection');
      if (grid) {
        observer.observe(grid, { childList: true, subtree: true });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
